// Importaciones de interfaces.
import { ILicense } from '../interfaces/license.interface';
import { IProduct } from '../interfaces/product.interface';

// Importaciones de modelos.
import { Product } from './product.model';

/**
 * Clase que representa una licencia.
 *
 * Esta clase contiene información relevante sobre una licencia, incluyendo
 * el nombre, el alias, tipo y el identificador de la licencia dentro de la aplicación.
 */
export class License implements ILicense {
  /**
   * Identificador único de la licencia.
   *
   * Este campo representa un identificador único asignado a la licencia.
   * Puede ser un número o nulo si no está asignado.
   */
  id: number | null;

  /**
   * Producto de la licencia.
   *
   * Este campo define el producto asignado a la licencia.
   * El producto puede ser un objeto que implementa la interfaz `IProduct`.
   */
  product: IProduct;

  /**
   * Alias de la licencia.
   *
   * Este campo almacena un alias que identifica la licencia de manera alternativa.
   * Puede ser nulo si no se ha establecido.
   */
  alias: string | null;

  /**
   * Nombre de la licencia.
   *
   * Este campo almacena el nombre de la licencia.
   * Puede ser nulo si no se ha establecido.
   */
  name: string | null;

  /**
   * Tipo de la licencia.
   *
   * Este campo define el tipo de licencia.
   * Puede ser nulo si no se ha establecido.
   */
  type: string | null;

  /**
   * El estado de la licencia, indicando si está habilitado o deshabilitado.
   * Puede ser `null` si no se especifica el estado.
   */
  disabled: number;

  /**
   * Fecha de última actualización.
   *
   * Indica el momento en que la licencia fue modificada por última vez.
   * Es útil para el seguimiento de cambios y auditorías.
   */
  updatedAt: string | null;

  /**
   * Fecha de creación.
   *
   * Representa el instante en que la licencia fue registrada por primera vez.
   * Es importante para conocer su antigüedad y gestionar su validez.
   */
  createdAt: string | null;

  /**
   * Constructor de la clase que inicializa una instancia de ILicense.
   *
   * @param {Partial<ILicense>} license - Un objeto parcial de ILicense que contiene las siguientes propiedades opcionales:
   *   - `id` (opcional): Identificador de la licencia. Por defecto, `null`.
   *   - `productId` (opcional): Identificador del producto. Por defecto, `null`.
   *   - `alias` (opcional): Nombre alternativo o identificador secundario. Por defecto, `null`.
   *   - `name` (opcional): Nombre de la licencia. Por defecto, `null`.
   *   - `type` (opcional): Tipo de licencia. Por defecto, `null`.
   *   - `updatedAt` (opcional): Fecha de última actualización. Por defecto, `null`.
   *   - `createdAt` (opcional): Fecha de creación. Por defecto, `null`.
   */
  constructor({
    id = null,
    product = new Product(),
    alias = null,
    name = null,
    type = null,
    disabled = 0,
    updatedAt = null,
    createdAt = null,
  }: Partial<ILicense> = {}) {
    this.id = id;
    this.product = product;
    this.alias = alias;
    this.name = name;
    this.type = type;
    this.disabled = disabled;
    this.updatedAt = updatedAt;
    this.createdAt = createdAt;
  }
}
